import { useState, useEffect } from 'react';
import { api } from '../api/client';

interface PairingCodeModalProps {
  onClose: () => void;
}

/**
 * PairingCodeModal - Displays generated pairing code with countdown timer.
 *
 * Generates code on mount via POST /auth/pair/generate (requires admin auth).
 * Timer counts down from 5:00 to 0:00; visual warning (coral) when <1 minute remaining.
 * Copy button uses Clipboard API for easy code transfer.
 */
export function PairingCodeModal({ onClose }: PairingCodeModalProps) {
  const [code, setCode] = useState<string | null>(null);
  const [expiresIn, setExpiresIn] = useState(300);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Generate code immediately on mount; admin authentication handled by existing session
    api.post<{ code: string; expiresIn: number }>('/auth/pair/generate')
      .then((res) => {
        setCode(res.code);
        setExpiresIn(res.expiresIn);
      })
      .catch((err) => {
        console.error('Failed to generate pairing code:', err);
        setError('Failed to generate code');
      });
  }, []);

  useEffect(() => {
    if (expiresIn <= 0) return;
    // 1-second timer for countdown display; stops at 0 (no negative values)
    const timer = setInterval(() => {
      setExpiresIn((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [expiresIn]);

  const handleCopy = () => {
    if (code) {
      navigator.clipboard.writeText(code);
      setCopied(true);
      // 2-second feedback before reverting button text
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-xl p-6 max-w-sm w-full mx-4" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-semibold text-text-primary mb-4">Kiosk Pairing Code</h3>

        {error ? (
          <p className="text-coral">{error}</p>
        ) : code ? (
          <>
            <p className="text-sm text-text-secondary mb-4">
              Enter this code on your kiosk device to sign in:
            </p>
            {/* 4xl monospace (36px): readable from 10+ feet for kiosk viewing distance */}
            {/* tracking-widest (0.1em spacing): prevents misreading similar digits (0/6, 6/8) during transcription */}
            <div className="text-4xl font-mono font-bold text-center tracking-widest py-4 bg-gray-50 rounded-lg">
              {code}
            </div>
            <div className="flex items-center justify-between mt-4">
              {/* Coral warning when <60s remaining to signal urgency */}
              <span className={`text-sm ${expiresIn < 60 ? 'text-coral' : 'text-text-secondary'}`}>
                Expires in {formatTime(expiresIn)}
              </span>
              <button
                onClick={handleCopy}
                disabled={copied}
                className="px-3 py-1 text-sm bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-50"
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </>
        ) : (
          <p className="text-text-secondary">Generating code...</p>
        )}

        <button
          onClick={onClose}
          className="mt-6 w-full py-3 text-text-secondary hover:text-text-primary"
        >
          Close
        </button>
      </div>
    </div>
  );
}
